// PaymentPage.js - Complete Improved Version
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../dashboard_component';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactPaginate from 'react-paginate';
import { billingAPI, paymentAPI } from '../../api';
function PaymentPage() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('organization');
  const [selectedPlanType, setSelectedPlanType] = useState('monthly');
  const [paymentMethod, setPaymentMethod] = useState('paystack');
  const [currency, setCurrency] = useState('NGN');
  const [exchangeRate, setExchangeRate] = useState(1500);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const handleToggle = () => setIsCollapsed(!isCollapsed);

  // Plans state fetched from API
  const [plans, setPlans] = useState(null);

  // Fetch plans from API
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await paymentAPI.getPlans();
        if (response.success && response.data && response.data.data) {
          setPlans(response.data.data); // Updated: access response.data.data
        } else {
          console.warn('Failed to fetch plans from API');
          setPlans(null);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        setPlans(null);
      }
    };

    fetchPlans();
  }, []);

  // Fetch data from APIs
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch exchange rate
        const rateResponse = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const rateData = await rateResponse.json();
        setExchangeRate(rateData.rates.NGN);
        
        // Fetch payment history
        try {
          const historyResponse = await paymentAPI.getPaymentHistory({ limit: 100 });
          if (historyResponse.success && historyResponse.data) {
            setPaymentHistory(Array.isArray(historyResponse.data) ? historyResponse.data : historyResponse.data.payments || []);
          } else {
            setPaymentHistory([]);
          }
        } catch (historyError) {
          console.warn('Failed to fetch payment history:', historyError);
          setPaymentHistory([]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load data. Please try again later.');
        toast.error('Failed to load payment data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 60 * 60 * 1000); // Refresh hourly

    return () => clearInterval(interval);
  }, []);

  // Safety check - show loading if plans not ready
  if (isLoading || !plans) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
        <div className={`flex-1 ${isCollapsed ? 'ml-20' : 'ml-64'} transition-all duration-300`}>
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="mb-4">
                <i className="fas fa-spinner fa-spin text-4xl text-purple-600"></i>
              </div>
              <p className="text-gray-600">Loading subscription plans...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getFormattedPrice = () => {
    if (!plans?.[selectedCategory]?.[selectedPlanType]) {
      return 'N/A';
    }
    
    const priceInUSD = plans[selectedCategory][selectedPlanType].basePriceUSD;
    
    if (paymentMethod === 'crypto') {
      return `${priceInUSD} USDT`;
    }
    
    if (currency === 'NGN') {
      return `₦${(priceInUSD * exchangeRate).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }

    return `$${priceInUSD}`;
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setError(null);

    try {
      if (!plans || !plans[selectedCategory] || !plans[selectedCategory][selectedPlanType]) {
        setError('Plans not loaded. Please refresh and try again.');
        return;
      }

      const currentPlan = plans[selectedCategory][selectedPlanType];
      
      // Create invoice using API
      const invoiceResponse = await paymentAPI.createInvoice({
        plan: selectedCategory === 'organization' ? `org_${selectedPlanType}` : `norm_${selectedPlanType}`,
        category: selectedCategory,
        plan_type: selectedPlanType,
        payment_method: paymentMethod,
        currency: currency,
        amount: currentPlan.basePriceUSD
      });

      if (!invoiceResponse.success) {
        setError(invoiceResponse.error || 'Failed to create invoice');
        toast.error(invoiceResponse.error || 'Failed to create invoice');
        setIsProcessing(false);
        return;
      }

      console.log('Invoice created:', invoiceResponse.data);
      setInvoiceDetails(invoiceResponse.data);
      toast.success('Invoice created successfully!');
      
      // For bank payments, show invoice details
      if (paymentMethod === 'bank') {
        setIsProcessing(false);
        return;
      }
      
      // For crypto payments, navigate to crypto payment page
      if (paymentMethod === 'crypto') {
        navigate(`/cbt_creator/crypto-payment/${invoiceResponse.data.invoice_id}`, {
          state: invoiceResponse.data.crypto_details ? {
            wallet_address: invoiceResponse.data.crypto_details?.wallet_address,
            amount: invoiceResponse.data.crypto_details?.amount,
            currency: invoiceResponse.data.crypto_details?.currency,
            network: invoiceResponse.data.crypto_details?.network,
          } : invoiceResponse.data
        });
      }
      
      // Redirect to payment gateway for other methods
      if (paymentMethod === 'paystack' || paymentMethod === 'flutterwave') {
        toast.info('Redirecting to payment gateway...');
        setTimeout(() => {
          if (invoiceResponse.data.payment_url) {
            window.location.href = invoiceResponse.data.payment_url;
          } else {
            toast.error('Payment URL not found');
            setIsProcessing(false);
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Payment error:', err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || 'Payment processing failed';
      
      // Show specific error for USD non-crypto
      if (errorMsg.includes('USD payments require')) {
        setError('Please select Crypto payment method for USD payments');
        setPaymentMethod('crypto');
        toast.error('USD payments require crypto method. Crypto payment selected.');
      } else {
        setError(errorMsg);
        toast.error(errorMsg);
      }
      setIsProcessing(false);
    }
  };

  const handleBankTransferConfirmation = async () => {
    try {
      setIsProcessing(true);
      
      // Confirm bank transfer using API
      const confirmResponse = await paymentAPI.confirmBankTransfer({
        invoice_id: invoiceDetails.invoice_id,
        reference_number: `BANK_${Date.now()}`,
        notes: 'Confirmed via web interface'
      });

      if (!confirmResponse.success) {
        setError(confirmResponse.error || 'Failed to confirm payment');
        toast.error(confirmResponse.error || 'Failed to confirm payment');
        setIsProcessing(false);
        return;
      }

      toast.success('Bank transfer confirmed! Your payment is being verified.');
      
      // Refresh payment history
      const historyResponse = await paymentAPI.getPaymentHistory({ limit: 100 });
      if (historyResponse.success && historyResponse.data) {
        setPaymentHistory(Array.isArray(historyResponse.data) ? historyResponse.data : historyResponse.data.payments || []);
      }
      setInvoiceDetails(null);
      
    } catch (err) {
      console.error('Confirmation error:', err);
      const errorMsg = err.data?.detail || err.message || 'Confirmation failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Pagination logic
  const pageCount = Math.ceil(paymentHistory.length / itemsPerPage);
  const offset = currentPage * itemsPerPage;
  const currentPayments = paymentHistory.slice(offset, offset + itemsPerPage);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
      case 'success':
        return <span className="bg-gradient-to-r from-green-100 to-green-50 text-green-800 px-3 py-1.5 rounded-full text-xs font-medium">Success</span>;
      case 'processing':
        return <span className="bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 px-3 py-1.5 rounded-full text-xs font-medium">Processing</span>;
      case 'pending':
        return <span className="bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 px-3 py-1.5 rounded-full text-xs font-medium">Pending</span>;
      case 'failed':
        return <span className="bg-gradient-to-r from-red-100 to-red-50 text-red-800 px-3 py-1.5 rounded-full text-xs font-medium">Failed</span>;
      default:
        return <span className="bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 px-3 py-1.5 rounded-full text-xs font-medium">Unknown</span>;
    }
  };

  const getCategoryIcon = (category) => {
    if (category === 'organization') {
      return (
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-3 rounded-2xl shadow-lg">
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="bg-gradient-to-br from-teal-500 to-emerald-600 p-3 rounded-2xl shadow-lg">
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
      );
    }
  };

  const handleManageBilling = () => {
    navigate('/cbt_creator/billing');
  };

  // const handleViewInvoice = (invoiceId) => {
  //   navigate(`https://cbthost.com/payment/receipt/${invoiceId}`);
  // };

  const handleViewInvoice = (invoiceId) => {
  window.open(
    `https://cbthost.com/payment/receipt/${invoiceId}`,
    "_blank"
  );
};


  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      <ToastContainer position="bottom-right" autoClose={5000} />

      <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Modern Header with Billing Navigation */}
          <div className="mb-10 flex flex-col lg:flex-row justify-between items-start lg:items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent mb-4">
                Subscription Plans
              </h1>
              <p className="text-gray-600 text-lg max-w-3xl">
                Choose the perfect plan for your needs. Administrators get access to advanced analytics, unlimited users, and priority support.
              </p>
            </div>
            <button
              // onClick={handleManageBilling}
              className="mt-4 lg:mt-0 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-xl font-semibold flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <i className="fas fa-credit-card"></i>
              Manage Billing & Credits
            </button>
          </div>

          {error && (
            <div className="mb-6 bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-xl p-4">
              <div className="flex items-center">
                <i className="fas fa-exclamation-circle text-red-500 mr-3"></i>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          )}

          {invoiceDetails && paymentMethod === 'bank' ? (
            // Bank Transfer Confirmation UI
            <div className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-lg border border-blue-100 p-6">
              <div className="flex items-start space-x-6">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-2xl shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">Bank Transfer Instructions</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                      <h4 className="font-bold text-gray-700 mb-4 text-lg">Account Details</h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Bank Name</p>
                          <p className="font-semibold text-gray-800">{invoiceDetails.bank_details?.bank_name || "First Bank"}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Account Name</p>
                          <p className="font-semibold text-gray-800">{invoiceDetails.bank_details?.account_name || "CBTHost Technologies"}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Account Number</p>
                          <p className="font-semibold text-gray-800 text-lg">{invoiceDetails.bank_details?.account_number || "3091234567"}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Reference</p>
                          <p className="font-mono font-bold text-blue-600">{invoiceDetails.invoice_id}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                      <h4 className="font-bold text-gray-700 mb-4 text-lg">Payment Information</h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Amount</p>
                          <p className="text-2xl font-bold text-gray-800">{getFormattedPrice()}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Plan</p>
                          <p className="font-semibold text-gray-800 capitalize">
                            {selectedCategory === 'organization' ? 'Organization' : 'Normal'} - {selectedPlanType}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Invoice ID</p>
                          <p className="font-mono text-gray-800">{invoiceDetails.invoice_id}</p>
                        </div>
                        <div className="flex items-center">
                          <p className="text-sm text-gray-500 mr-3">Status:</p>
                          <span className="px-3 py-1.5 bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 rounded-full text-sm font-medium">
                            Pending Confirmation
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 mb-4">
                    <button
                      onClick={handleBankTransferConfirmation}
                      disabled={isProcessing}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3.5 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-75 flex items-center justify-center"
                    >
                      {isProcessing ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Confirming...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          I've Sent the Transfer
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setInvoiceDetails(null)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-3.5 px-8 rounded-xl transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    After completing your bank transfer, click "I've Sent the Transfer" to notify us.
                    Your account will be upgraded within 24 hours after we verify the payment.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Regular Payment UI
            <>
              {/* Category Selection */}
              <div className="mb-10">
                <h3 className="text-xl font-bold text-gray-800 mb-6">Select Account Type</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {['normal', 'organization'].map((category) => (
                    <div
                      key={category}
                      className={`rounded-2xl p-6 cursor-pointer transition-all duration-300 border-2 ${
                        selectedCategory === category
                          ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg'
                          : 'border-gray-200 hover:border-blue-300 bg-white hover:shadow-md'
                      }`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      <div className="flex items-start space-x-4 mb-4">
                        {getCategoryIcon(category)}
                        <div>
                          <h4 className="text-2xl font-bold text-gray-800 mb-1 capitalize">
                            {category === 'organization' ? 'Organization (Admin/Analytics)' : 'Normal Account'}
                          </h4>
                          <p className="text-gray-600">
                            {category === 'organization' 
                              ? 'For institutions with advanced analytics and unlimited users' 
                              : 'For regular system usage without analytics'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Plan Type Selection within Category */}
                      <div className="flex space-x-4 mb-6">
                        {['monthly', 'yearly'].map((type) => (
                          <button
                            key={type}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPlanType(type);
                            }}
                            className={`px-5 py-2.5 rounded-xl font-medium transition-all ${
                              selectedPlanType === type && selectedCategory === category
                                ? type === 'yearly' 
                                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                                  : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                            {type === 'yearly' && (
                              <span className="ml-2 text-xs bg-white/30 px-2 py-0.5 rounded-full">
                                Save 40%
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                      
                      {/* Plan Details */}
                      <div className="mb-6">
                        {plans?.[category]?.[selectedPlanType] ? (
                          <>
                            <div className="flex items-baseline mb-4">
                              <span className="text-3xl font-bold text-gray-800">
                                {(() => {
                                  const price = plans[category][selectedPlanType].basePriceUSD;
                                  if (currency === 'NGN') {
                                    return `₦${(price * exchangeRate).toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2
                                    })}`;
                                  }
                                  return `$${price}`;
                                })()}
                              </span>
                              <span className="text-gray-500 ml-2">/{plans[category][selectedPlanType].duration}</span>
                            </div>
                            
                            <ul className="space-y-2.5">
                              {plans[category][selectedPlanType].features?.map((feature, index) => (
                                <li key={index} className="flex items-start">
                                  <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                  <span className="text-gray-700">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </>
                        ) : (
                          <p className="text-gray-500">Plan details unavailable</p>
                        )}
                      </div>
                      
                      {selectedCategory === category && (
                        <div className="text-center pt-4 border-t border-gray-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (selectedCategory === category) {
                                document.getElementById('payment-section').scrollIntoView({ behavior: 'smooth' });
                              }
                            }}
                            className="text-blue-600 hover:text-blue-800 font-semibold flex items-center justify-center"
                          >
                            Select this plan
                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Selected Plan Summary */}
              {plans?.[selectedCategory]?.[selectedPlanType] ? (
                <div className="mb-10 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-sm p-6 border border-blue-100">
                  <div className="flex flex-col lg:flex-row justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Selected Plan</h3>
                      <p className="text-gray-600">
                        <span className="font-bold capitalize">{selectedCategory === 'organization' ? 'Organization' : 'Normal'} - {selectedPlanType}</span>
                        <span className="mx-2">•</span>
                        {plans[selectedCategory][selectedPlanType].duration}
                        <span className="mx-2">•</span>
                        <span className="text-2xl font-bold text-blue-600">{getFormattedPrice()}</span>
                      </p>
                    </div>
                    <div className="mt-4 lg:mt-0">
                      <span className="inline-flex items-center px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Selected
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mb-10 bg-yellow-50 rounded-2xl shadow-sm p-6 border border-yellow-200">
                  <p className="text-yellow-700">Loading plan details...</p>
                </div>
              )}

              <div id="payment-section">
                {/* Currency and Payment Method */}
                <div className="mb-10">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Currency Selector */}
                    <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                      <h3 className="text-xl font-bold text-gray-800 mb-4">Currency Settings</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Select Currency</label>
                          <select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                            disabled={isProcessing}
                          >
                            <option value="NGN">Naira (₦)</option>
                            <option value="USD">US Dollar ($)</option>
                          </select>
                        </div>
                        {!isLoading && !error && (
                          <div className="bg-gray-50 rounded-xl p-4">
                            <p className="text-sm text-gray-600 mb-1">Current Exchange Rate</p>
                            <p className="text-lg font-bold text-gray-800">1 USD = {exchangeRate.toLocaleString()} NGN</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Payment Methods */}
                    <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                      <h3 className="text-xl font-bold text-gray-800 mb-4">Payment Method</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { id: 'paystack', name: 'Paystack', icon: '💳', color: 'from-blue-500 to-blue-600' },
                          { id: 'bank', name: 'Bank Transfer', icon: '🏦', color: 'from-green-500 to-emerald-600' },
                          { id: 'crypto', name: 'USDT-ERC20', icon: '💎', color: 'from-green-500 to-emerald-600' },
                        ].map((method) => (
                          <div
                            key={method.id}
                            className={`cursor-pointer rounded-xl p-4 border-2 transition-all ${
                              paymentMethod === method.id
                                ? `border-blue-500 bg-gradient-to-br ${method.color} bg-opacity-10`
                                : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                            }`}
                            onClick={() => setPaymentMethod(method.id)}
                          >
                            <div className="flex flex-col items-center">
                              <div className={`text-2xl mb-2 ${paymentMethod === method.id ? 'opacity-100' : 'opacity-70'}`}>
                                {method.icon}
                              </div>
                              <span className={`font-medium ${paymentMethod === method.id ? 'text-blue-600' : 'text-gray-700'}`}>
                                {method.name}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-gray-500 mt-4">
                        {paymentMethod === 'bank' 
                          ? 'Bank transfer may take 24-48 hours to process' 
                          : paymentMethod === 'crypto'
                          ? 'Crypto payments require confirmation on blockchain'
                          : 'Paystack provides instant payment confirmation'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Final Payment Section */}
                <div className="mb-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl shadow-xl p-8 text-white">
                  <div className="flex flex-col lg:flex-row justify-between items-center">
                    <div className="mb-6 lg:mb-0">
                      <h3 className="text-2xl font-bold mb-2">Ready to Upgrade?</h3>
                      <p className="text-blue-100">
                        Your analytics dashboard will be activated within 24 hours after payment confirmation
                      </p>
                    </div>
                    <div className="text-center lg:text-right">
                      <p className="text-blue-100 mb-2">Total Amount</p>
                      <p className="text-4xl font-bold mb-4">{getFormattedPrice()}</p>
                      <button
                        onClick={handlePaymentSubmit}
                        disabled={isProcessing}
                        className="bg-white text-blue-600 hover:bg-blue-50 font-bold py-4 px-10 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-75"
                      >
                        {isProcessing ? (
                          <div className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Processing Payment...
                          </div>
                        ) : (
                          <>
                            <svg className="inline-block w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Proceed to Payment
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Payment History */}
          <div className="mt-16">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Payment History</h2>
                <p className="text-gray-600">Track all your subscription payments and invoices</p>
              </div>
              {paymentHistory.length > 0 && (
                <span className="text-sm text-gray-500">
                  Showing {Math.min(offset + 1, paymentHistory.length)}-
                  {Math.min(offset + itemsPerPage, paymentHistory.length)} of {paymentHistory.length}
                </span>
              )}
            </div>
            
            {isLoading ? (
              <div className="flex justify-center py-16">
                <div className="text-center">
                  <svg className="animate-spin h-12 w-12 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="mt-4 text-gray-600">Loading payment history...</p>
                </div>
              </div>
            ) : paymentHistory.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-100">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No payment history found</h3>
                <p className="text-gray-500 max-w-md mx-auto">Your payment history will appear here once you make your first subscription payment.</p>
              </div>
            ) : (
              <>
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Method</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentPayments.map((payment, index) => (
                        <tr key={payment.invoice_id || payment.id || index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">#{payment.invoice_id || payment.id}</div>
                              <div className="text-xs text-gray-500 capitalize">{payment.plan || 'Standard'}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-bold text-gray-900">
                              {payment.currency === 'NGN' ? '₦' : '$'}{payment.amount}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {new Date(payment.date || payment.created_at).toLocaleDateString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 capitalize">{payment.method || payment.payment_method}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(payment.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex space-x-3">
                              {payment.receipt_url && (
                                <a 
                                  href={payment.receipt_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  Receipt
                                </a>
                              )}
                              {payment.status === 'pending' && payment.payment_url && (
                                <a 
                                  // href={`https://cbthost.com/payment/receipt/${payment.payment_url}`} 
                                  href={`https://cbthost.com/payment/receipt/${payment.invoice_id || payment.id}`} 
                                  className="text-green-600 hover:text-green-800 font-medium flex items-center"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                  </svg>
                                  Complete Payment
                                </a>
                              )}
                              <button
                                onClick={() => handleViewInvoice(payment.invoice_id || payment.id)}
                                className="text-purple-600 hover:text-purple-800 font-medium flex items-center"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {pageCount > 1 && (
                  <div className="mt-8">
                    <ReactPaginate
                      previousLabel={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      }
                      nextLabel={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      }
                      pageCount={pageCount}
                      onPageChange={handlePageClick}
                      containerClassName="flex justify-center items-center space-x-2"
                      pageLinkClassName="flex items-center justify-center w-10 h-10 rounded-xl border border-gray-300 hover:bg-gray-100 text-gray-700 font-medium"
                      activeLinkClassName="bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-500"
                      previousLinkClassName="flex items-center justify-center w-10 h-10 rounded-xl border border-gray-300 hover:bg-gray-100"
                      nextLinkClassName="flex items-center justify-center w-10 h-10 rounded-xl border border-gray-300 hover:bg-gray-100"
                      disabledLinkClassName="opacity-30 cursor-not-allowed"
                      breakLabel="..."
                      breakClassName="px-2 text-gray-500"
                      marginPagesDisplayed={1}
                      pageRangeDisplayed={3}
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Support Section */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="flex flex-col lg:flex-row justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Need help with your subscription?</h3>
                <p className="text-gray-600">
                  Contact our support team for assistance with payments, billing, or subscription questions.
                </p>
              </div>
              <div className="mt-4 lg:mt-0 flex gap-4">
                <a
                  href="https://wa.me/2348113315252"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-xl font-semibold"
                >
                  <i className="fab fa-whatsapp text-lg"></i>
                  Chat on WhatsApp
                </a>
                <button
                  onClick={() => navigate('/cbt_creator/help')}
                  className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-xl font-semibold"
                >
                  <i className="fas fa-question-circle"></i>
                  Help Center
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentPage;